import { createDefaultTenantFeatures } from "../../shared/tenant";
import type {
  TenantContextValue,
  TenantFeatureKey,
  TenantFeatures,
  TenantRole,
  TenantStatus,
  TenantSummary,
} from "./types";

export type AppPageKey =
  | "monitor"
  | "sources"
  | "roomList"
  | "businessUnits"
  | "stats"
  | "dispatchScriptParse"
  | "dispatchScriptBind"
  | "dispatchScriptProgress"
  | "dispatchScriptMaterial"
  | "messages"
  | "groupChat"
  | "speakerRoster"
  | "speechPoints"
  | "users"
  | "tenants"
  | "atmosphereProfile"
  | "atmospherePersonaCategory"
  | "scriptSheet"
  | "scriptRoleReviewRecords"
  | "aiProvider"
  | "aiModel"
  | "settings"
  | "security"
  | "profile"
  | "churnMembers";

const DISPATCH_MONITOR_PAGES = new Set<AppPageKey>([
  "monitor",
  "sources",
  "roomList",
  "businessUnits",
  "stats",
  "messages",
  "groupChat",
  "speakerRoster",
  "churnMembers",
]);

const SCRIPT_MONITOR_PAGES = new Set<AppPageKey>([
  "dispatchScriptParse",
  "dispatchScriptBind",
  "dispatchScriptProgress",
  "dispatchScriptMaterial",
]);

const ATMOSPHERE_PAGES = new Set<AppPageKey>([
  "atmosphereProfile",
  "atmospherePersonaCategory",
  "scriptSheet",
  "scriptRoleReviewRecords",
]);

const AI_PAGES = new Set<AppPageKey>(["aiProvider", "aiModel"]);

const TENANT_ADMIN_PAGES = new Set<AppPageKey>([
  "sources",
  "roomList",
  "businessUnits",
  "stats",
  "dispatchScriptParse",
  "dispatchScriptBind",
  "messages",
  "groupChat",
  "speakerRoster",
  "speechPoints",
  "users",
  "aiProvider",
  "aiModel",
  "scriptRoleReviewRecords",
  "churnMembers",
]);

export function isTenantActiveStatus(status: TenantStatus | string | null | undefined) {
  if (!status) return false;
  return String(status).toLowerCase() === "active";
}

export function isTenantAdminRole(role: TenantRole | string | null | undefined) {
  return role === "tenant_admin" || role === "platform_super_admin";
}

export function isOperatingAwayFromHome(
  context: Pick<TenantContextValue, "homeTenant" | "activeTenant"> | null | undefined
) {
  if (!context?.homeTenant?.id || !context?.activeTenant?.id) return false;
  return context.homeTenant.id !== context.activeTenant.id;
}

export function normalizeFeatures(
  features?: Partial<TenantFeatures> | null
): TenantFeatures {
  return createDefaultTenantFeatures(features || undefined);
}

export function hasFeature(
  features: TenantFeatures | null | undefined,
  key: TenantFeatureKey
) {
  return Boolean(features?.[key]);
}

export function getFeatureForPage(page: AppPageKey): TenantFeatureKey | null {
  if (DISPATCH_MONITOR_PAGES.has(page)) return "dispatch_monitor";
  if (SCRIPT_MONITOR_PAGES.has(page)) return "script_monitor";
  if (ATMOSPHERE_PAGES.has(page)) return "atmosphere_assistant";
  if (page === "speechPoints") return "speech_points";
  if (AI_PAGES.has(page)) return "ai_management";
  return null;
}

export interface PageAccessInput {
  page: AppPageKey;
  role: TenantRole | string | null | undefined;
  isPlatformSuperAdmin: boolean;
  features: TenantFeatures;
  scriptAccess?: {
    atmosphereGroups: string[];
    personaCategoryEnabled: boolean;
    feishuScriptEnabled: boolean;
  };
}

export function canAccessAppPage(input: PageAccessInput): boolean {
  const { page, role, isPlatformSuperAdmin, features, scriptAccess } = input;
  const isAdmin = isTenantAdminRole(role) || isPlatformSuperAdmin;

  if (page === "tenants") {
    return Boolean(isPlatformSuperAdmin);
  }

  if (page === "users") {
    return isAdmin;
  }

  const featureKey = getFeatureForPage(page);
  if (featureKey && !hasFeature(features, featureKey)) {
    return false;
  }

  if (page === "atmosphereProfile") {
    return isAdmin || Boolean(scriptAccess?.atmosphereGroups.length);
  }
  if (page === "atmospherePersonaCategory") {
    return isAdmin || Boolean(scriptAccess?.personaCategoryEnabled);
  }
  if (page === "scriptSheet") {
    return isAdmin || Boolean(scriptAccess?.feishuScriptEnabled);
  }
  if (page === "scriptRoleReviewRecords") {
    return isAdmin;
  }

  if (TENANT_ADMIN_PAGES.has(page)) {
    return isAdmin;
  }

  // 成员可看发单情况与素材列表，仍受 script_monitor feature 约束
  if (page === "dispatchScriptProgress" || page === "dispatchScriptMaterial") {
    return true;
  }

  return true;
}

export function getDefaultLandingPage(input: Omit<PageAccessInput, "page">): AppPageKey {
  const candidates: AppPageKey[] = [
    "monitor",
    "dispatchScriptProgress",
    "atmosphereProfile",
    "scriptSheet",
    "settings",
    "profile",
  ];
  for (const page of candidates) {
    if (canAccessAppPage({ ...input, page })) return page;
  }
  return "settings";
}

export function formatTenantLabel(tenant: TenantSummary | null | undefined) {
  if (!tenant) return "未选择租户";
  if (tenant.code) return `${tenant.name} (${tenant.code})`;
  return tenant.name || tenant.id;
}

export function roleLabel(role: TenantRole | string | null | undefined) {
  switch (role) {
    case "platform_super_admin":
      return "平台超级管理员";
    case "tenant_admin":
      return "租户管理员";
    case "member":
      return "成员";
    default:
      return role || "未知角色";
  }
}
